/**
 * @file Shared React component — components/common/ChangePasswordModal.tsx.
 */

import React, { useState } from "react";
import axiosClient from "../../api/axiosClient";
import "./ChangePasswordModal.css";
import Button from "./Button";
import OutlineButton from "./OutlineButton";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(""); // Clear error on input change
  };

  const validateForm = () => {
    if (!formData.currentPassword) {
      setError("Current password is required");
      return false;
    }
    if (!formData.newPassword) {
      setError("New password is required");
      return false;
    }
    if (formData.newPassword.length < 8) {
      setError("New password must be at least 8 characters long");
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError("New passwords do not match");
      return false;
    }
    if (formData.currentPassword === formData.newPassword) {
      setError("New password must be different from current password");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await axiosClient.post("/profile/change-password", {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      if (response.data?.success) {
        setSuccess(true);
        // Reset form
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        
        // Close modal after 1.5 seconds
        setTimeout(() => {
          setSuccess(false);
          onClose();
          if (onSuccess) {
            onSuccess();
          }
        }, 1500);
      } else {
        setError(response.data?.message || "Failed to change password");
      }
    } catch (err: any) {
      console.error("Change password error:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to change password. Please check your current password and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setError("");
      setSuccess(false);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={handleClose}>
      <div className="modal-card change-password-modal fade-in" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="modal-header">
          <h2>Change Password</h2>
          <button
            className="modal-close"
            onClick={handleClose}
            disabled={loading}
            type="button"
          >
            ×
          </button>
        </div>

        {/* CONTENT */}
        <div className="modal-body">
          {success ? (
            <div className="success-message">
              <div className="success-icon">✓</div>
              <p>Password changed successfully!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="change-password-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label htmlFor="currentPassword">Current Password *</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  placeholder="Enter your current password"
                  autoComplete="current-password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password *</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  placeholder="Enter your new password (min. 8 characters)"
                  autoComplete="new-password"
                  minLength={8}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password *</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  placeholder="Confirm your new password"
                  autoComplete="new-password"
                />
              </div>

              {/* FOOTER */}
              <div className="modal-footer">
                <OutlineButton
                  label="Cancel"
                  onClick={handleClose}
                  disabled={loading}
                  type="button"
                />
                <Button
                  label={loading ? "Changing..." : "Change Password"}
                  disabled={loading}
                  type="submit"
                />
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/** React component — Change Password Modal. */
export default ChangePasswordModal;

